// AnalyticsContext.js - PostHog Analytics Provider
import React, { createContext, useContext, useEffect, useCallback } from 'react';
import posthog from 'posthog-js';
import { useAuth } from './AuthContext';
import { ANALYTICS_CONFIG, isAnalyticsEnabled } from '../config/analytics';

const AnalyticsContext = createContext(null);

export const AnalyticsProvider = ({ children }) => {
  const { currentUser } = useAuth();

  // Initialize PostHog on mount
  useEffect(() => {
    if (!isAnalyticsEnabled()) {
      console.info('Analytics disabled - set VITE_ENABLE_ANALYTICS=true to enable');
      return;
    }

    posthog.init(ANALYTICS_CONFIG.apiKey, ANALYTICS_CONFIG.options);

    return () => {
      posthog.reset(); // Clear on unmount
    };
  }, []);

  // Identify user when authenticated
  useEffect(() => {
    if (!isAnalyticsEnabled()) return;

    if (currentUser) {
      // Identify user with PostHog
      posthog.identify(currentUser.id.toString(), {
        email: currentUser.email,
        username: currentUser.username,
        role: currentUser.role,
        team_ids: currentUser.teams?.map(t => t.id) || [],
        team_names: currentUser.teams?.map(t => t.name) || [],
        color: currentUser.color,
        created_at: currentUser.created_at,
        weekly_hours: currentUser.weekly_hours,
      });

      // Set super properties that will be included with all events
      posthog.register({
        user_role: currentUser.role,
        user_id: currentUser.id,
      });
    } else {
      // Clear identity on logout
      posthog.reset();
    }
  }, [currentUser]);

  /**
   * Track a page view
   */
  const trackPageView = useCallback((pageName, properties = {}) => {
    if (!isAnalyticsEnabled()) return;

    posthog.capture('$pageview', {
      $current_url: window.location.href,
      page_name: pageName,
      ...properties,
    });
  }, []);

  /**
   * Track a custom event
   */
  const trackEvent = useCallback((eventName, properties = {}) => {
    if (!isAnalyticsEnabled()) return;

    posthog.capture(eventName, properties);
  }, []);

  /**
   * Track timing/performance metrics
   */
  const trackTiming = useCallback((category, variable, timeMs, properties = {}) => {
    if (!isAnalyticsEnabled()) return;

    posthog.capture('timing', {
      category,
      variable,
      time_ms: timeMs,
      ...properties,
    });
  }, []);

  /**
   * Set a user property
   */
  const setUserProperty = useCallback((property, value) => {
    if (!isAnalyticsEnabled()) return;

    posthog.people.set({ [property]: value });
  }, []);

  /**
   * Start session recording manually
   */
  const startRecording = useCallback(() => {
    if (!isAnalyticsEnabled()) return;
    posthog.startSessionRecording();
  }, []);

  /**
   * Stop session recording manually (for sensitive operations)
   */
  const stopRecording = useCallback(() => {
    if (!isAnalyticsEnabled()) return;
    posthog.stopSessionRecording();
  }, []);

  const value = {
    trackPageView,
    trackEvent,
    trackTiming,
    setUserProperty,
    startRecording,
    stopRecording,
    isEnabled: isAnalyticsEnabled(),
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};

/**
 * Hook to access analytics functions
 */
export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within AnalyticsProvider');
  }
  return context;
};
